import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import html2canvas from "html2canvas";
import JSZip from "jszip";


const ANCHOR_STORAGE_KEY = "qa-hero-anchor-v2";

type Breakpoint = "mobile" | "tablet" | "desktop";

const DEFAULTS: Record<Breakpoint, { x: number; y: number }> = {
  mobile: { x: 50, y: 30 },
  tablet: { x: 50, y: 34 },
  desktop: { x: 52, y: 42 },
};

type Viewport = {
  label: string;
  device: Breakpoint;
  width: number;
  height: number;
};

const VIEWPORTS: Viewport[] = [
  { label: "iPhone SE", device: "mobile", width: 375, height: 667 },
  { label: "iPhone 13/14", device: "mobile", width: 390, height: 844 },
  { label: "iPhone 14 Pro Max", device: "mobile", width: 430, height: 932 },
  { label: "Pixel 7", device: "mobile", width: 412, height: 915 },
  { label: "iPad Mini (portrait)", device: "tablet", width: 768, height: 1024 },
  { label: "iPad Pro 11 (portrait)", device: "tablet", width: 834, height: 1194 },
  { label: "iPad Pro 11 (landscape)", device: "tablet", width: 1194, height: 834 },
  { label: "Laptop", device: "desktop", width: 1280, height: 800 },
  { label: "MacBook Pro 14", device: "desktop", width: 1512, height: 982 },
  { label: "Desktop HD", device: "desktop", width: 1440, height: 900 },
  { label: "Desktop FHD", device: "desktop", width: 1920, height: 1080 },
];

const GROUPS: Breakpoint[] = ["mobile", "tablet", "desktop"];

type GuideOptions = {
  crosshair: boolean;
  thirds: boolean;
  safeArea: boolean;
  labels: boolean;
};

const GUIDE_DEFAULTS: GuideOptions = {
  crosshair: true,
  thirds: false,
  safeArea: false,
  labels: true,
};

/**
 * Paints optional anchor guides (crosshair, rule-of-thirds grid, safe-area box)
 * onto an exported screenshot canvas so face centering can be verified offline.
 */
function drawGuides(
  canvas: HTMLCanvasElement,
  anchor: { x: number; y: number },
  guides: GuideOptions,
  meta: { label: string; device: Breakpoint; width: number; height: number },
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const cx = (anchor.x / 100) * w;
  const cy = (anchor.y / 100) * h;

  ctx.save();
  ctx.lineWidth = Math.max(1, Math.round(w / 900));

  if (guides.thirds) {
    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.setLineDash([6, 6]);
    ctx.beginPath();
    for (const f of [1 / 3, 2 / 3]) {
      ctx.moveTo(f * w, 0);
      ctx.lineTo(f * w, h);
      ctx.moveTo(0, f * h);
      ctx.lineTo(w, f * h);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  if (guides.safeArea) {
    const inset = 0.1;
    ctx.strokeStyle = "rgba(56,189,248,0.75)";
    ctx.setLineDash([10, 6]);
    ctx.strokeRect(inset * w, inset * h, w - 2 * inset * w, h - 2 * inset * h);
    ctx.setLineDash([]);
  }

  if (guides.crosshair) {
    // Full-bleed crosshair through the anchor point.
    ctx.strokeStyle = "rgba(244,63,94,0.85)";
    ctx.beginPath();
    ctx.moveTo(cx, 0);
    ctx.lineTo(cx, h);
    ctx.moveTo(0, cy);
    ctx.lineTo(w, cy);
    ctx.stroke();

    // Target rings at the anchor.
    ctx.strokeStyle = "rgba(250,204,21,0.95)";
    const r = Math.max(10, Math.round(Math.min(w, h) * 0.045));
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.moveTo(cx + r / 2.4, cy);
    ctx.arc(cx, cy, r / 2.4, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (guides.labels) {
    const text = `${meta.device} ${meta.width}×${meta.height} · anchor ${anchor.x.toFixed(1)}% / ${anchor.y.toFixed(1)}% · ${meta.label}`;
    const fontSize = Math.max(11, Math.round(w / 90));
    ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
    const pad = Math.round(fontSize * 0.6);
    const tw = ctx.measureText(text).width;
    ctx.fillStyle = "rgba(2,6,23,0.78)";
    ctx.fillRect(pad, pad, tw + pad * 2, fontSize + pad * 1.4);
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    ctx.textBaseline = "top";
    ctx.fillText(text, pad * 2, pad * 1.6);
  }

  ctx.restore();
}

type AnchorSet = Record<Breakpoint, { x: number; y: number }>;

type Snapshot = {
  id: string;
  name: string;
  anchors: AnchorSet;
  createdAt: string;
};

const SNAPSHOT_STORAGE_KEY = "qa-hero-snapshots-v1";
const WORKING_ANCHORS_KEY = "qa-hero-working-anchors-v1";

function loadWorkingAnchors(): AnchorSet {
  try {
    const raw = localStorage.getItem(WORKING_ANCHORS_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<AnchorSet>;
    const merged = { ...DEFAULTS } as AnchorSet;
    (Object.keys(DEFAULTS) as Breakpoint[]).forEach((k) => {
      const v = parsed?.[k];
      if (v && typeof v.x === "number" && typeof v.y === "number") merged[k] = v;
    });
    return merged;
  } catch {
    return DEFAULTS;
  }
}

/** Renders one viewport in an offscreen iframe and returns its canvas. */
async function captureFrame(
  host: HTMLElement,
  vp: Viewport,
  anchorSet: AnchorSet,
): Promise<HTMLCanvasElement | null> {
  const a = anchorSet[vp.device];
  const q = `?x=${encodeURIComponent(`${a.x}%`)}&y=${encodeURIComponent(`${a.y}%`)}`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = `width:${vp.width}px;height:${vp.height}px;border:0;display:block;`;
  iframe.src = `/qa/hero/frame${q}`;
  host.appendChild(iframe);
  try {
    await new Promise<void>((resolve) => {
      iframe.addEventListener("load", () => resolve(), { once: true });
    });
    // Give the hero image + fonts a moment to settle inside the frame.
    await new Promise((r) => setTimeout(r, 450));
    const doc = iframe.contentDocument;
    if (!doc?.documentElement) return null;
    return await html2canvas(doc.documentElement, {
      width: vp.width,
      height: vp.height,
      windowWidth: vp.width,
      windowHeight: vp.height,
      backgroundColor: null,
      useCORS: true,
      scale: 1,
    });
  } finally {
    host.removeChild(iframe);
  }
}

const toBlobAsync = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((res) => canvas.toBlob((b) => res(b), "image/png"));

/**
 * Pixel-diffs two same-size captures. Returns a canvas where unchanged pixels
 * are dimmed grayscale and changed pixels are highlighted magenta, plus the
 * fraction of pixels that differ beyond `threshold` (0-255 per channel).
 */
function diffCanvases(
  a: HTMLCanvasElement,
  b: HTMLCanvasElement,
  threshold = 18,
): { canvas: HTMLCanvasElement; changedRatio: number } | null {
  const w = Math.min(a.width, b.width);
  const h = Math.min(a.height, b.height);
  const ac = a.getContext("2d");
  const bc = b.getContext("2d");
  if (!w || !h || !ac || !bc) return null;
  const ad = ac.getImageData(0, 0, w, h);
  const bd = bc.getImageData(0, 0, w, h);
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const octx = out.getContext("2d");
  if (!octx) return null;
  const od = octx.createImageData(w, h);
  let changed = 0;
  for (let i = 0; i < ad.data.length; i += 4) {
    const dr = Math.abs(ad.data[i] - bd.data[i]);
    const dg = Math.abs(ad.data[i + 1] - bd.data[i + 1]);
    const db = Math.abs(ad.data[i + 2] - bd.data[i + 2]);
    const delta = Math.max(dr, dg, db);
    if (delta > threshold) {
      changed++;
      od.data[i] = 255;
      od.data[i + 1] = 0;
      od.data[i + 2] = 200;
      od.data[i + 3] = 255;
    } else {
      const gray =
        0.299 * ad.data[i] + 0.587 * ad.data[i + 1] + 0.114 * ad.data[i + 2];
      const dim = Math.round(gray * 0.45 + 30);
      od.data[i] = dim;
      od.data[i + 1] = dim;
      od.data[i + 2] = dim;
      od.data[i + 3] = 255;
    }
  }
  octx.putImageData(od, 0, 0);
  return { canvas: out, changedRatio: changed / (w * h) };
}



/**
 * Visual QA harness. Each device group has its own anchor preset that maps
 * to the matching `--hero-anchor-*-{mobile|tablet|desktop}` CSS variable in
 * `src/index.css`. The active `--hero-anchor-x/y` is switched at 640px and
 * 1024px media queries so the face stays centered per breakpoint.
 */
export default function QAHero() {
  const [scale, setScale] = useState(0.5);
  const [showRulers, setShowRulers] = useState(true);
  const [anchors, setAnchors] = useState<AnchorSet>(loadWorkingAnchors);
  const [exporting, setExporting] = useState<null | { done: number; total: number }>(null);
  const [selected, setSelected] = useState<string[]>(() => VIEWPORTS.map((v) => v.label));
  const [guides, setGuides] = useState<GuideOptions>(GUIDE_DEFAULTS);
  const guidesEnabled = guides.crosshair || guides.thirds || guides.safeArea || guides.labels;
  const toggleGuide = (key: keyof GuideOptions) =>
    setGuides((prev) => ({ ...prev, [key]: !prev[key] }));
  const captureHostRef = useRef<HTMLDivElement | null>(null);

  // ---- Tuning snapshots + compare mode -------------------------------------
  const [snapshots, setSnapshots] = useState<Snapshot[]>(() => {
    try {
      const raw = localStorage.getItem(SNAPSHOT_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Snapshot[]) : [];
    } catch {
      return [];
    }
  });
  const [compareMode, setCompareMode] = useState(false);
  const [compareA, setCompareA] = useState<string>("");
  const [compareB, setCompareB] = useState<string>("");
  const [snapshotName, setSnapshotName] = useState("");
  const [diffSummary, setDiffSummary] = useState<
    { label: string; changedRatio: number }[]
  >([]);
  const [activePresetId, setActivePresetId] = useState<string>("");
  const importInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshots));
    } catch {
      /* storage unavailable */
    }
  }, [snapshots]);

  useEffect(() => {
    try {
      localStorage.setItem(WORKING_ANCHORS_KEY, JSON.stringify(anchors));
    } catch {
      /* storage unavailable */
    }
  }, [anchors]);

  const cloneAnchors = (a: AnchorSet) => JSON.parse(JSON.stringify(a)) as AnchorSet;
  const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const saveSnapshot = () => {
    const name = snapshotName.trim() || `Preset ${snapshots.length + 1}`;
    const existing = snapshots.find(
      (s) => s.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing) {
      setSnapshots((prev) =>
        prev.map((s) =>
          s.id === existing.id
            ? { ...s, anchors: cloneAnchors(anchors), createdAt: new Date().toISOString() }
            : s,
        ),
      );
      setActivePresetId(existing.id);
      setSnapshotName("");
      return;
    }
    const snap: Snapshot = {
      id: newId(),
      name,
      anchors: cloneAnchors(anchors),
      createdAt: new Date().toISOString(),
    };
    setSnapshots((prev) => [...prev, snap]);
    setSnapshotName("");
    setActivePresetId(snap.id);
    if (!compareA) setCompareA(snap.id);
    else if (!compareB) setCompareB(snap.id);
  };

  const loadSnapshot = (s: Snapshot) => {
    setAnchors(cloneAnchors(s.anchors));
    setActivePresetId(s.id);
    setSnapshotName(s.name);
  };

  const renameSnapshot = (id: string) => {
    const current = snapshots.find((s) => s.id === id);
    if (!current) return;
    const next = window.prompt("Rename preset", current.name)?.trim();
    if (!next) return;
    setSnapshots((prev) =>
      prev.map((s) => (s.id === id ? { ...s, name: next } : s)),
    );
  };

  const duplicateSnapshot = (id: string) => {
    const current = snapshots.find((s) => s.id === id);
    if (!current) return;
    const copy: Snapshot = {
      ...current,
      id: newId(),
      name: `${current.name} copy`,
      anchors: cloneAnchors(current.anchors),
      createdAt: new Date().toISOString(),
    };
    setSnapshots((prev) => [...prev, copy]);
  };

  const deleteSnapshot = (id: string) => {
    setSnapshots((prev) => prev.filter((s) => s.id !== id));
    if (compareA === id) setCompareA("");
    if (compareB === id) setCompareB("");
    if (activePresetId === id) setActivePresetId("");
  };

  const exportPresets = () => {
    const blob = new Blob(
      [JSON.stringify({ version: 1, presets: snapshots }, null, 2)],
      { type: "application/json" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hero-anchor-presets-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importPresets = async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text()) as
        | { presets?: Snapshot[] }
        | Snapshot[];
      const incoming = Array.isArray(parsed) ? parsed : (parsed.presets ?? []);
      const valid = incoming
        .filter((p) => p && p.anchors && p.name)
        .map((p) => ({
          id: newId(),
          name: p.name,
          anchors: { ...DEFAULTS, ...p.anchors } as AnchorSet,
          createdAt: p.createdAt ?? new Date().toISOString(),
        }));
      if (!valid.length) {
        window.alert("No valid presets found in that file.");
        return;
      }
      setSnapshots((prev) => [...prev, ...valid]);
    } catch {
      window.alert("Could not read that presets file.");
    }
  };


  const snapA = snapshots.find((s) => s.id === compareA) ?? null;
  const snapB = snapshots.find((s) => s.id === compareB) ?? null;
  const canCompare = !!snapA && !!snapB && snapA.id !== snapB.id;

  const handleCompareExport = async () => {
    const host = captureHostRef.current;
    if (!host || !snapA || !snapB) return;
    const targets = VIEWPORTS.filter((v) => selected.includes(v.label));
    if (targets.length === 0) return;

    const zip = new JSZip();
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const folder = zip.folder(`hero-compare-${stamp}`)!;
    const summary: { label: string; changedRatio: number }[] = [];
    const lines: string[] = [
      `A: ${snapA.name}`,
      `B: ${snapB.name}`,
      "",
      "viewport,device,size,changed_pixels_pct",
    ];

    setExporting({ done: 0, total: targets.length });
    try {
      for (let i = 0; i < targets.length; i++) {
        const vp = targets[i];
        const safe = vp.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
        const base = `${String(i + 1).padStart(2, "0")}-${vp.device}-${vp.width}x${vp.height}-${safe}`;

        const canvasA = await captureFrame(host, vp, snapA.anchors);
        const canvasB = await captureFrame(host, vp, snapB.anchors);
        if (canvasA && canvasB) {
          const diff = diffCanvases(canvasA, canvasB);
          if (guidesEnabled) {
            drawGuides(canvasA, snapA.anchors[vp.device], guides, {
              label: `${vp.label} · A ${snapA.name}`,
              device: vp.device,
              width: vp.width,
              height: vp.height,
            });
            drawGuides(canvasB, snapB.anchors[vp.device], guides, {
              label: `${vp.label} · B ${snapB.name}`,
              device: vp.device,
              width: vp.width,
              height: vp.height,
            });
          }
          const [blobA, blobB] = await Promise.all([
            toBlobAsync(canvasA),
            toBlobAsync(canvasB),
          ]);
          if (blobA) folder.file(`${base}/a-${base}.png`, blobA);
          if (blobB) folder.file(`${base}/b-${base}.png`, blobB);
          if (diff) {
            const blobD = await toBlobAsync(diff.canvas);
            if (blobD) folder.file(`${base}/diff-${base}.png`, blobD);
            summary.push({ label: vp.label, changedRatio: diff.changedRatio });
            lines.push(
              `${vp.label},${vp.device},${vp.width}x${vp.height},${(diff.changedRatio * 100).toFixed(2)}`,
            );
          }
        }
        setExporting({ done: i + 1, total: targets.length });
      }

      folder.file("diff-summary.csv", lines.join("\n") + "\n");
      setDiffSummary(summary);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hero-compare-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
    }
  };

  const isSelected = (label: string) => selected.includes(label);
  const toggleViewport = (label: string) =>
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  const setGroupSelected = (group: Breakpoint, on: boolean) =>
    setSelected((prev) => {
      const labels = VIEWPORTS.filter((v) => v.device === group).map((v) => v.label);
      return on
        ? Array.from(new Set([...prev, ...labels]))
        : prev.filter((l) => !labels.includes(l));
    });
  const groupAllSelected = (group: Breakpoint) =>
    VIEWPORTS.filter((v) => v.device === group).every((v) => isSelected(v.label));

  const selectedViewports = VIEWPORTS.filter((v) => isSelected(v.label));

  const handleExport = async () => {
    const host = captureHostRef.current;
    if (!host) return;
    const targets = VIEWPORTS.filter((v) => selected.includes(v.label));
    if (targets.length === 0) return;
    const zip = new JSZip();
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const folder = zip.folder(`hero-qa-${stamp}`)!;
    folder.file(
      "anchors.css",
      cssSnippet + "\n\n/* Exported " + new Date().toISOString() + " */\n",
    );

    setExporting({ done: 0, total: targets.length });
    try {
      for (let i = 0; i < targets.length; i++) {
        const vp = targets[i];
        const canvas = await captureFrame(host, vp, anchors);
        if (canvas) {
          const blob = await toBlobAsync(canvas);
          const safe = vp.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
          const base = `${String(i + 1).padStart(2, "0")}-${vp.device}-${vp.width}x${vp.height}-${safe}`;
          if (blob) folder.file(`${base}.png`, blob);

          if (guidesEnabled) {
            drawGuides(canvas, anchors[vp.device], guides, {
              label: vp.label,
              device: vp.device,
              width: vp.width,
              height: vp.height,
            });
            const guideBlob = await toBlobAsync(canvas);
            if (guideBlob) folder.file(`guides/${base}-guides.png`, guideBlob);
          }
        }
        setExporting({ done: i + 1, total: targets.length });
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hero-qa-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(null);
    }
  };

  /**
   * Compiles the selected viewports into a single review PDF: a cover page with
   * the anchor values, one page per screenshot (fitted, with caption), and a
   * final page containing the copy-paste anchors.css snippet.
   */
  const handleExportPdf = async () => {
    const host = captureHostRef.current;
    if (!host) return;
    const targets = VIEWPORTS.filter((v) => selected.includes(v.label));
    if (targets.length === 0) return;

    const { default: JsPDF } = await import("jspdf");
    const pdf = new JsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 36;
    const stamp = new Date();

    // Cover page
    pdf.setFontSize(20);
    pdf.text("Hero Anchor QA Review", margin, margin + 12);
    pdf.setFontSize(10);
    pdf.text(`Generated ${stamp.toLocaleString()}`, margin, margin + 34);
    pdf.text(`Viewports: ${targets.length}`, margin, margin + 50);
    let cy = margin + 76;
    pdf.setFontSize(12);
    pdf.text("Anchors", margin, cy);
    pdf.setFontSize(10);
    cy += 18;
    for (const g of GROUPS) {
      pdf.text(
        `${g}: x ${anchors[g].x.toFixed(1)}%  ·  y ${anchors[g].y.toFixed(1)}%`,
        margin + 12,
        cy,
      );
      cy += 16;
    }

    setExporting({ done: 0, total: targets.length });
    try {
      for (let i = 0; i < targets.length; i++) {
        const vp = targets[i];
        const canvas = await captureFrame(host, vp, anchors);
        if (canvas) {
          if (guidesEnabled) {
            drawGuides(canvas, anchors[vp.device], guides, {
              label: vp.label,
              device: vp.device,
              width: vp.width,
              height: vp.height,
            });
          }
          const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
          const captionH = 26;
          const availW = pageW - margin * 2;
          const availH = pageH - margin * 2 - captionH;
          const ratio = Math.min(availW / canvas.width, availH / canvas.height);
          const w = canvas.width * ratio;
          const h = canvas.height * ratio;

          pdf.addPage();
          pdf.setFontSize(11);
          pdf.text(
            `${vp.label} · ${vp.device} · ${vp.width}×${vp.height} · anchor ${anchors[vp.device].x.toFixed(1)}% / ${anchors[vp.device].y.toFixed(1)}%`,
            margin,
            margin,
          );
          pdf.addImage(
            dataUrl,
            "JPEG",
            margin + (availW - w) / 2,
            margin + captionH,
            w,
            h,
          );
        }
        setExporting({ done: i + 1, total: targets.length });
      }

      // CSS snippet page
      pdf.addPage();
      pdf.setFontSize(14);
      pdf.text("anchors.css", margin, margin + 6);
      pdf.setFontSize(9);
      const lines = pdf.splitTextToSize(cssSnippet, pageW - margin * 2) as string[];
      pdf.text(lines, margin, margin + 30);

      pdf.save(
        `hero-qa-${stamp.toISOString().replace(/[:.]/g, "-").slice(0, 19)}.pdf`,
      );
    } finally {
      setExporting(null);
    }
  };


  useEffect(() => {
    try {
      const raw = localStorage.getItem(ANCHOR_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setAnchors((prev) => ({
          mobile: { ...prev.mobile, ...(parsed.mobile ?? {}) },
          tablet: { ...prev.tablet, ...(parsed.tablet ?? {}) },
          desktop: { ...prev.desktop, ...(parsed.desktop ?? {}) },
        }));
      }
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(ANCHOR_STORAGE_KEY, JSON.stringify(anchors));
    } catch {
      /* ignore */
    }
  }, [anchors]);

  const cssSnippet = useMemo(
    () =>
      `:root {\n` +
      `  --hero-anchor-x-mobile: ${anchors.mobile.x}%;\n` +
      `  --hero-anchor-y-mobile: ${anchors.mobile.y}%;\n` +
      `  --hero-anchor-x-tablet: ${anchors.tablet.x}%;\n` +
      `  --hero-anchor-y-tablet: ${anchors.tablet.y}%;\n` +
      `  --hero-anchor-x-desktop: ${anchors.desktop.x}%;\n` +
      `  --hero-anchor-y-desktop: ${anchors.desktop.y}%;\n` +
      `}`,
    [anchors],
  );

  const anchorQueryFor = (bp: Breakpoint) => {
    const { x, y } = anchors[bp];
    return `?x=${encodeURIComponent(`${x}%`)}&y=${encodeURIComponent(`${y}%`)}`;
  };

  return (
    <>
      <Helmet>
        <title>Hero Portrait — Visual QA</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="min-h-screen bg-navy-deep text-foreground">
        <header className="sticky top-0 z-20 border-b border-border bg-navy-deep/95 backdrop-blur px-6 py-4">
          <div className="mx-auto max-w-7xl flex flex-wrap items-center gap-4 justify-between">
            <div>
              <h1 className="font-display text-xl tracking-wide">
                Hero Portrait · Visual QA
                <a
                  href="/qa/hero/perf"
                  className="ml-3 align-middle text-xs text-gold underline"
                >
                  LCP dashboard →
                </a>
              </h1>
              <p className="text-xs text-silver/70">
                Per-breakpoint anchors. Tune mobile / tablet / desktop
                independently — the CSS snippet below maps 1:1 to
                <code className="mx-1 text-gold">src/index.css</code>.
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-silver/80">
              <label className="flex items-center gap-2">
                Preview scale
                <input
                  type="range"
                  min={0.25}
                  max={1}
                  step={0.05}
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                />
                <span className="tabular-nums w-10">{Math.round(scale * 100)}%</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showRulers}
                  onChange={(e) => setShowRulers(e.target.checked)}
                />
                Show rulers
              </label>
              <button
                type="button"
                onClick={() => setAnchors(DEFAULTS)}
                className="px-3 py-1.5 rounded border border-border hover:bg-navy-elevated"
              >
                Reset all
              </button>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(cssSnippet)}
                className="px-3 py-1.5 rounded border border-gold/50 text-gold hover:bg-gold/10"
                title="Copy CSS to paste into src/index.css"
              >
                Copy CSS
              </button>
              <div
                className="flex items-center gap-3 rounded border border-border/60 px-3 py-1.5"
                title="Guide overlays are added to a second copy of each screenshot in the ZIP (guides/ folder)"
              >
                <span className="text-silver/70">Export guides:</span>
                {(
                  [
                    ["crosshair", "Crosshair"],
                    ["thirds", "Thirds"],
                    ["safeArea", "Safe area"],
                    ["labels", "Label"],
                  ] as [keyof GuideOptions, string][]
                ).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={guides[key]}
                      onChange={() => toggleGuide(key)}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <button
                type="button"
                onClick={handleExport}
                disabled={!!exporting || selectedViewports.length === 0}
                className="px-3 py-1.5 rounded border border-emerald/60 text-emerald hover:bg-emerald/10 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Capture the selected viewports at native size and download a ZIP of PNGs"
              >
                {exporting
                  ? `Exporting ${exporting.done}/${exporting.total}…`
                  : `Export ZIP (${selectedViewports.length})`}
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={!!exporting || selectedViewports.length === 0}
                className="px-3 py-1.5 rounded border border-gold/60 text-gold hover:bg-gold/10 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Compile the selected viewports into one reviewable PDF with the anchors.css snippet"
              >
                {exporting
                  ? `Building ${exporting.done}/${exporting.total}…`
                  : `Export PDF (${selectedViewports.length})`}
              </button>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={compareMode}
                  onChange={(e) => setCompareMode(e.target.checked)}
                />
                Compare mode
              </label>
            </div>
          </div>

          <div className="mx-auto max-w-7xl mt-4 rounded border border-gold/40 bg-navy-elevated/40 p-3 text-xs text-silver/80">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span className="font-display uppercase tracking-[0.25em] text-[10px] text-gold">
                Anchor presets
              </span>
              <input
                value={snapshotName}
                onChange={(e) => setSnapshotName(e.target.value)}
                placeholder="Preset name"
                className="rounded border border-border bg-transparent px-2 py-1"
              />
              <button
                type="button"
                onClick={saveSnapshot}
                className="px-3 py-1.5 rounded border border-gold/50 text-gold hover:bg-gold/10"
                title="Saves current anchors; reusing an existing name overwrites that preset"
              >
                Save preset
              </button>
              <button
                type="button"
                onClick={() => setAnchors(DEFAULTS)}
                className="px-3 py-1.5 rounded border border-border text-silver/80 hover:bg-white/5"
              >
                Reset to defaults
              </button>
              <span className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={exportPresets}
                  disabled={snapshots.length === 0}
                  className="px-3 py-1.5 rounded border border-border text-silver/80 hover:bg-white/5 disabled:opacity-50"
                >
                  Export presets (.json)
                </button>
                <button
                  type="button"
                  onClick={() => importInputRef.current?.click()}
                  className="px-3 py-1.5 rounded border border-border text-silver/80 hover:bg-white/5"
                >
                  Import presets
                </button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void importPresets(f);
                    e.target.value = "";
                  }}
                />
              </span>
            </div>

            {snapshots.length === 0 ? (
              <p className="text-silver/60">
                No saved presets yet — tune the anchors, name them, and save to
                reuse the settings in a later session.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {snapshots.map((s) => (
                  <li
                    key={s.id}
                    className={`flex items-center gap-2 rounded border px-2 py-1 ${
                      activePresetId === s.id
                        ? "border-gold/70 bg-gold/10"
                        : "border-border/60"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => loadSnapshot(s)}
                      className="hover:text-gold"
                      title="Load this preset into the live anchors"
                    >
                      {s.name}
                    </button>
                    <span className="text-silver/50 tabular-nums">
                      {GROUPS.map(
                        (g) =>
                          `${g[0]}:${s.anchors[g].x.toFixed(0)}/${s.anchors[g].y.toFixed(0)}`,
                      ).join(" ")}
                    </span>
                    <button
                      type="button"
                      onClick={() => renameSnapshot(s.id)}
                      className="text-silver/60 hover:text-silver"
                      aria-label={`Rename preset ${s.name}`}
                      title="Rename"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      onClick={() => duplicateSnapshot(s.id)}
                      className="text-silver/60 hover:text-silver"
                      aria-label={`Duplicate preset ${s.name}`}
                      title="Duplicate"
                    >
                      ⧉
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSnapshot(s.id)}
                      className="text-rose-400 hover:text-rose-300"
                      aria-label={`Delete preset ${s.name}`}
                      title="Delete"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {compareMode && (
            <div className="mx-auto max-w-7xl mt-4 rounded border border-gold/40 bg-navy-elevated/40 p-3 text-xs text-silver/80">
              {snapshots.length < 2 ? (
                <p className="text-silver/60">
                  Save at least two presets to compare tunings.
                </p>
              ) : (
                <div className="flex flex-wrap items-end gap-4">
                  {(
                    [
                      ["A", compareA, setCompareA],
                      ["B", compareB, setCompareB],
                    ] as [string, string, (v: string) => void][]
                  ).map(([side, value, setter]) => (
                    <label key={side} className="flex flex-col gap-1">
                      <span className="text-silver/70">Preset {side}</span>
                      <select
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                        className="rounded border border-border bg-navy-elevated px-2 py-1"
                      >
                        <option value="">— select —</option>
                        {snapshots.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={handleCompareExport}
                    disabled={!canCompare || !!exporting || selectedViewports.length === 0}
                    className="px-3 py-1.5 rounded border border-emerald/60 text-emerald hover:bg-emerald/10 disabled:opacity-60 disabled:cursor-not-allowed"
                    title="Capture A and B for each selected viewport and export A/B/diff images"
                  >
                    {exporting
                      ? `Comparing ${exporting.done}/${exporting.total}…`
                      : `Export comparison (${selectedViewports.length})`}
                  </button>
                </div>
              )}


              {diffSummary.length > 0 && (
                <div className="mt-3 border-t border-border/60 pt-3">
                  <p className="mb-1 text-silver/70">
                    Last comparison — magenta pixels in each diff image mark
                    changed regions:
                  </p>
                  <ul className="flex flex-wrap gap-x-4 gap-y-1">
                    {diffSummary.map((d) => (
                      <li key={d.label} className="tabular-nums">
                        {d.label}:{" "}
                        <span
                          className={
                            d.changedRatio > 0.02 ? "text-gold" : "text-silver/60"
                          }
                        >
                          {(d.changedRatio * 100).toFixed(2)}% changed
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mx-auto max-w-7xl mt-4 rounded border border-border/60 bg-navy-elevated/40 p-3 text-xs text-silver/80">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <span className="font-display uppercase tracking-[0.25em] text-[10px] text-gold">
                Export viewports
              </span>
              {GROUPS.map((bp) => (
                <button
                  key={bp}
                  type="button"
                  onClick={() => setGroupSelected(bp, !groupAllSelected(bp))}
                  aria-pressed={groupAllSelected(bp)}
                  className={
                    "px-2.5 py-1 rounded border capitalize " +
                    (groupAllSelected(bp)
                      ? "border-emerald/60 text-emerald bg-emerald/10"
                      : "border-border text-silver/70 hover:bg-navy-elevated")
                  }
                >
                  {bp}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setSelected(VIEWPORTS.map((v) => v.label))}
                className="px-2.5 py-1 rounded border border-border hover:bg-navy-elevated"
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setSelected([])}
                className="px-2.5 py-1 rounded border border-border hover:bg-navy-elevated"
              >
                None
              </button>
              <span className="text-silver/50">
                {selectedViewports.length} of {VIEWPORTS.length} selected
              </span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              {VIEWPORTS.map((vp) => (
                <label key={vp.label} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isSelected(vp.label)}
                    onChange={() => toggleViewport(vp.label)}
                  />
                  <span>{vp.label}</span>
                  <span className="tabular-nums text-silver/45">
                    {vp.width}×{vp.height}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mx-auto max-w-7xl mt-4 grid gap-4 md:grid-cols-3 text-xs text-silver/80">
            {GROUPS.map((bp) => (
              <div
                key={bp}
                className="rounded border border-border/60 p-3 space-y-2 bg-navy-elevated/40"
              >
                <div className="font-display uppercase tracking-[0.25em] text-[10px] text-gold">
                  {bp}
                </div>
                {(["x", "y"] as const).map((axis) => (
                  <label key={axis} className="flex items-center gap-2">
                    <span className="w-8 shrink-0 uppercase">{axis}</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={0.5}
                      value={anchors[bp][axis]}
                      onChange={(e) =>
                        setAnchors((prev) => ({
                          ...prev,
                          [bp]: {
                            ...prev[bp],
                            [axis]: parseFloat(e.target.value),
                          },
                        }))
                      }
                      className="flex-1"
                    />
                    <span className="tabular-nums w-12 text-right">
                      {anchors[bp][axis].toFixed(1)}%
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <pre className="mx-auto max-w-7xl mt-2 text-[10px] text-silver/50 whitespace-pre-wrap">
{cssSnippet}
          </pre>
        </header>


        <div className="mx-auto max-w-7xl px-6 py-8 space-y-12">
          {GROUPS.map((group) => {
            const items = VIEWPORTS.filter((v) => v.device === group);
            const q = anchorQueryFor(group);
            return (
              <section key={group} className="space-y-4">
                <h2 className="font-display uppercase tracking-[0.3em] text-xs text-gold">
                  {group} · anchor {anchors[group].x.toFixed(1)}% /{" "}
                  {anchors[group].y.toFixed(1)}%
                </h2>
                <p className="text-[11px] text-silver/60 -mt-2">
                  Tip: drag the gold dot on any preview to reposition the face for this breakpoint. Arrow keys nudge (Shift = 5%).
                </p>
                <div className="flex flex-wrap gap-8">
                  {items.map((vp) => (
                    <QAFrame
                      key={vp.label}
                      viewport={vp}
                      scale={scale}
                      showRulers={showRulers}
                      anchor={anchors[group]}
                      anchorQuery={q}
                      selected={isSelected(vp.label)}
                      onToggleSelected={() => toggleViewport(vp.label)}
                      onAnchorChange={(next) =>
                        setAnchors((prev) => ({ ...prev, [group]: next }))
                      }
                    />

                  ))}
                </div>
              </section>
            );
          })}
        </div>
        {/* Offscreen host for native-size capture during export. */}
        <div
          ref={captureHostRef}
          aria-hidden
          style={{
            position: "fixed",
            left: "-100000px",
            top: 0,
            width: 0,
            height: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        />

      </main>
    </>
  );
}

function QAFrame({
  viewport,
  scale,
  showRulers,
  anchor,
  anchorQuery,
  selected,
  onToggleSelected,
  onAnchorChange,
}: {
  viewport: Viewport;
  scale: number;
  showRulers: boolean;
  anchor: { x: number; y: number };
  anchorQuery: string;
  selected: boolean;
  onToggleSelected: () => void;
  onAnchorChange: (next: { x: number; y: number }) => void;
}) {

  const { label, width, height } = viewport;
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragging = dragPos !== null;
  const displayed = dragPos ?? anchor;

  const clamp = (n: number) => Math.max(0, Math.min(100, n));
  const round1 = (n: number) => Math.round(n * 10) / 10;

  const pointFromEvent = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = stageRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      x: clamp(((e.clientX - rect.left) / rect.width) * 100),
      y: clamp(((e.clientY - rect.top) / rect.height) * 100),
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const p = pointFromEvent(e);
    if (p) setDragPos(p);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const p = pointFromEvent(e);
    if (p) setDragPos(p);
  };

  const handlePointerUp = () => {
    if (dragPos) onAnchorChange({ x: round1(dragPos.x), y: round1(dragPos.y) });
    setDragPos(null);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const step = e.shiftKey ? 5 : 1;
    let { x, y } = anchor;
    if (e.key === "ArrowLeft") x = clamp(x - step);
    else if (e.key === "ArrowRight") x = clamp(x + step);
    else if (e.key === "ArrowUp") y = clamp(y - step);
    else if (e.key === "ArrowDown") y = clamp(y + step);
    else return;
    e.preventDefault();
    e.stopPropagation();
    onAnchorChange({ x: round1(x), y: round1(y) });
  };

  return (
    <figure
      className="flex flex-col gap-2"
      style={{
        width: width * scale,
      }}
    >
      <figcaption className="flex items-baseline justify-between gap-2 text-xs text-silver/80">
        <label className="flex items-center gap-2 font-medium">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Include ${label} in export`}
          />
          <span className={selected ? "" : "text-silver/45"}>{label}</span>
        </label>
        <span className="tabular-nums text-silver/60">
          {width}×{height}
        </span>
      </figcaption>

      <div
        ref={stageRef}
        className="relative border border-border/60 shadow-portrait rounded-sm overflow-hidden bg-navy touch-none select-none"
        style={{
          width: width * scale,
          height: height * scale,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <iframe
          title={`Hero preview — ${label}`}
          src={`/qa/hero/frame${anchorQuery}`}
          style={{
            width,
            height,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
          loading="lazy"
        />
        {showRulers && (
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              className="absolute left-0 right-0 border-t border-emerald/70"
              style={{ top: `${(1 / 3) * 100}%` }}
            />
            <div
              className="absolute left-0 right-0 border-t border-emerald/30"
              style={{ top: `${(2 / 3) * 100}%` }}
            />
          </div>
        )}
        {dragging && (
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div
              className="absolute top-0 bottom-0 border-l border-gold/70"
              style={{ left: `${displayed.x}%` }}
            />
            <div
              className="absolute left-0 right-0 border-t border-gold/70"
              style={{ top: `${displayed.y}%` }}
            />
          </div>
        )}
        <button
          type="button"
          aria-label={`Focus point for ${label}. Drag or use arrow keys to nudge.`}
          onKeyDown={handleKey}
          className={
            "absolute z-10 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full " +
            "bg-gold/90 border-2 border-navy-deep shadow-lg " +
            "ring-2 ring-gold/40 hover:ring-gold/70 focus:outline-none focus:ring-4 focus:ring-gold " +
            (dragging ? "cursor-grabbing scale-110" : "cursor-grab")
          }
          style={{
            left: `${displayed.x}%`,
            top: `${displayed.y}%`,
            transition: dragging ? "none" : "left 120ms, top 120ms",
          }}
        />
        <div className="pointer-events-none absolute bottom-1 right-1 rounded bg-navy-deep/80 px-1.5 py-0.5 text-[10px] font-mono text-gold tabular-nums">
          {displayed.x.toFixed(1)} / {displayed.y.toFixed(1)}
        </div>
      </div>
    </figure>
  );
}
